/**
 * @author Nolan Bjarne Schiemann
 * @version 1.0.0
 * @copyright
 */

/**
 * Escaping a string in JavaScript.
 *
 * @returns {string}
 */
String.prototype.addSlashes = function () {
  return this.replace(/[\\"']/g, "\\$&").replace(/\u0000/g, "\\0");
};

/**
 * If applied to any input field it filters anything non-numerical out of the value and returns a numerical-only value.
 *
 * @param {string|number} value
 * @returns {number}
 */
function filterInputNumericValue(value) {
  return (value = value.replace(/[^\d]+/, "")).replace(/^0+(?!$)/, "");
}

/**
 * If applied to any input field it filters anything non-numerical out of the value and returns a numerical-only value.
 *
 * @param {string|number} value
 * @returns {number}
 */
function filterInputNumericValueAllowZero(value) {
  return (value = value.replace(/[^\d]+/, ""));
}

/**
 * Manually fire a click event.
 *
 * @param {HTMLElement} node
 */
function fireClick(node) {
  if (document.createEvent) {
    let evt = document.createEvent("MouseEvents");
    evt.initEvent("click", true, false);
    node.dispatchEvent(evt);
  } else if (document.createEventObject) {
    node.fireEvent("onclick");
  } else if (typeof node.onclick == "function") {
    node.onclick();
  }
}

/**
 * Manually fire a input event.
 *
 * @param {HTMLElement} node
 */
function fireInput(node) {
  if (document.createEventObject) {
    node.fireEvent("oninput");
  } else if (typeof node.oninput == "function") {
    node.oninput();
  }
}

/**
 * Gets an array fed and tries to find matches by comparing values and properties.
 *
 * @param {array} array
 * @param {*} property
 * @param {*} value
 * @returns {array}
 */
function getMatches(array, property, value) {
  if (!property || property === undefined || value === undefined) {
    return array;
  } else {
    return array.filter((el) => el[property] === value);
  }
}

/**
 * File reader which converts a given file into a base64 string.
 *
 * @param {File} file
 * @returns
 */
const reader = (file) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr);
    fr.onerror = (err) => reject(err);
    fr.readAsDataURL(file);
  });

/**
 * Get file contents.
 *
 * @param {File[]} file_list
 * @returns {array}
 */
async function getFileContents(file_list) {
  let file_results = [];
  const fr_promises = file_list.map(reader);

  try {
    file_results = await Promise.all(fr_promises);
  } catch (err) {
    // In this specific case, Promise.all() might be preferred
    // over Promise.allSettled(), since it isn't trivial to modify
    // a FileList to a subset of files of what the user initially
    // selected. Therefore, let's just stash the entire operation.
    console.error(err);
    return;
  }

  return file_results;
}

/**
 * Generate a random id.
 * The format of a generated id: 'aaaaaaaa'-'aaaa'-'aaaa'-'aaaa'-'aaaaaaaaaaaa'.
 *
 * @returns {string}
 */
const guid = function () {
  const s4 = function () {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };
  return (
    s4() +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    "-" +
    s4() +
    s4() +
    s4()
  );
};

/**
 * Check if an object is empty.
 *
 * @param {object} objectName
 * @returns {boolean}
 */
function isObjectEmpty(objectName) {
  return (
    objectName &&
    Object.keys(objectName).length === 0 &&
    objectName.constructor === Object
  );
}

/**
 * Checks an object if it includes a specified string.
 *
 * @param {object} object
 * @param {string} string
 * @returns {boolean}
 */
function includesObjectAString(object, string) {
  return Object.keys(object).some(function (e) {
    return object[e] === string;
  });
}

/**
 * Clamp value to be in the boundaries of a minimum and maximum.
 *
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return value <= min ? min : value >= max ? max : value;
}

/**
 * Create an HTML element.
 *
 * @param {string} type
 * @param {string} class_name
 * @param {string} content
 * @returns {HTMLElement}
 */
const createHTMLElement = function (type, class_name = "", content = "") {
  const element = document.createElement(type);
  if (!!class_name) {
    element.classList.add(...class_name.split(" "));
  }
  element.insertAdjacentHTML("beforeend", content);
  return element;
};

/**
 * Show an html element/ node.
 *
 * @param {HTMLElement} el
 * @returns
 */
function showHTMLElement(el) {
  if (!el.classList.contains("hide")) return;
  el.classList.remove("hide");
}

/**
 * Hide an html element/ node.
 *
 * @param {HTMLElement} el
 * @returns
 */
function hideHTMLElement(el) {
  if (el.classList.contains("hide")) return;
  el.classList.add("hide");
}

/**
 * Enable an element.
 *
 * @param {HTMLElement} el
 * @returns
 */
function enableHTMLElement(el) {
  const property = "disabled";
  if (el.hasAttribute(property)) {
    el.removeAttribute(property);
  }
  if (!el.classList.contains(property)) return;
  el.classList.remove(property);
}

/**
 * Disable an element.
 *
 * @param {HTMLElement} el
 * @returns
 */
function disableHTMLElement(el) {
  const property = "disabled";
  el.setAttribute(property, true);
  el.checked = false;
  if (el.classList.contains(property)) return;
  el.classList.add(property);
}

/**
 * Check if the local storage of the browser is available.
 *
 * @return {boolean} Availability of the local storage
 */
function isLocalStorageAvailable() {
  try {
    localStorage.setItem("check", "availability");
    localStorage.removeItem("check");
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Check if the session storage of the browser is available.
 *
 * @return {boolean} Availability of the session storage
 */
function isSessionStorageAvailable() {
  try {
    sessionStorage.setItem("check", "availability");
    sessionStorage.removeItem("check");
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Create a cookie item.
 * Make sure the user has given his consent to the usage of cookies.
 *
 * @param item
 * @param value
 */
function createCookie(item, value) {
  if (item === "" || value === "") return;
  if (isLocalStorageAvailable()) {
    localStorage.setItem(item, value);
  } else {
    createSessionCookie(item, value);
  }
}

/**
 * Delete a cookie item from the local storage.
 *
 * @param item
 */
function deleteCookie(item) {
  if (item === "") return;
  if (isLocalStorageAvailable() && localStorage.getItem(item) != null) {
    localStorage.removeItem(item);
  }
}

/**
 * Get a cookie item.
 *
 * @param item
 * @returns {any}
 */
function getCookie(item) {
  return getRawCookie(item);
}

/**
 * Get a cookie item (raw, cookie consent is not required)
 *
 * @param item
 * @returns {any}
 */
function getRawCookie(item) {
  if (isLocalStorageAvailable() && localStorage.getItem(item) != null) {
    return localStorage.getItem(item);
  } else {
    return getSessionCookie(item);
  }
}

/**
 * Get all cookie items of the localStorage and return them as a list.
 *
 * @returns {array}
 */
function getCookieList(includes = "") {
  let temp_cookie_list = [];
  for (let key in localStorage) {
    if (key.includes(includes)) {
      temp_cookie_list.push([String(key), getCookie(key)]);
    }
  }
  return temp_cookie_list;
}

/**
 * Create a session cookie item only.
 *
 * @param item
 * @param value
 */
function createSessionCookie(item, value) {
  if (item === "" || value === "") return;
  if (isSessionStorageAvailable()) {
    sessionStorage.setItem(item, value);
  }
}

/**
 * Get a session cookie item.
 *
 * @param item
 * @returns {any|null}
 */
function getSessionCookie(item) {
  if (isSessionStorageAvailable() && sessionStorage.getItem(item) != null) {
    return sessionStorage.getItem(item);
  }
  return null;
}

/**
 * Delete a session cookie item from the session storage.
 *
 * @param item
 */
function deleteSessionCookie(item) {
  if (item === "") return;
  if (isSessionStorageAvailable() && sessionStorage.getItem(item) != null) {
    sessionStorage.removeItem(item);
  }
}

/**
 * Iterate through all properties or entries of an object or array.
 *
 * @param {object|array} object
 * @param {string} name
 * @returns {array|object|undefined}
 */
function iterateThroughProperties(object, name) {
  if (!object || typeof object !== "object") {
    return;
  }
  for (const key in object) {
    const current_object = object[key];
    if (current_object.id === name) {
      return current_object;
    }
    const result = Array.isArray(current_object.sublist)
      ? iterateThroughProperties(current_object.sublist, name)
      : null;
    if (result) {
      return result;
    }
  }
}

/**
 * Update the quotes counter placed on the sidedrawer icon.
 */
function updateQuotesCount() {
  const quotes_handle_counter = document.querySelector(
    ".sidebar__quotes-handle > .count"
  );
  const cookie_count = getCookieList("sticks--request__quote").length;
  if (cookie_count > 9) {
    quotes_handle_counter.innerHTML = "9+";
  } else {
    quotes_handle_counter.innerHTML = cookie_count;
  }
}
